import jsPDF from 'jspdf'
import {COUNTRY_NAMES} from './constants'
import type {CalcResult, Country, Destination} from './types'
import {fmt} from './utils'

const FONT_NAME = 'Systemia'

async function loadFonts(doc: jsPDF) {
  const toBase64 = (buf: ArrayBuffer) => {
    const bytes = new Uint8Array(buf)
    let binary = ''

    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i])
    }

    return btoa(binary)
  }

  const [regular, bold] = await Promise.all([
    fetch('/fonts/Systemia-Regular.ttf').then((r) => r.arrayBuffer()),
    fetch('/fonts/Systemia-Bold.ttf').then((r) => r.arrayBuffer()),
  ])

  doc.addFileToVFS('Systemia-Regular.ttf', toBase64(regular))
  doc.addFont('Systemia-Regular.ttf', FONT_NAME, 'normal')

  doc.addFileToVFS('Systemia-Bold.ttf', toBase64(bold))
  doc.addFont('Systemia-Bold.ttf', FONT_NAME, 'bold')

  doc.setFont(FONT_NAME, 'normal')
}

const MOSS_GREEN: [number, number, number] = [0, 36, 20]
const BANNER_BG: [number, number, number] = [0, 41, 24] // #002918
const PILL_BG: [number, number, number] = [10, 61, 42] // #0a3d2a
const WHITE: [number, number, number] = [255, 255, 255]
const GREY_LABEL: [number, number, number] = [139, 139, 139]
const LIGHT_BORDER: [number, number, number] = [220, 220, 220]
const BODY_TEXT: [number, number, number] = [50, 50, 50]
const NEG_TEXT: [number, number, number] = [220, 60, 60]

const MOSS_LOGO_DARK_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="79" height="19" viewBox="0 0 79 19" fill="none"><path d="M35.2006 6.08917C36.2904 6.08917 36.9401 6.86459 36.9401 8.26872V14.7235H39.5178V8.49925C39.5178 7.13703 40.0627 6.08917 41.3411 6.08917C42.4308 6.08917 43.0805 6.86459 43.0805 8.26872V14.7235H45.7421V7.59809C45.7421 5.22993 44.2541 3.88867 42.2841 3.88867C40.8171 3.88867 39.8741 4.64313 39.2453 5.75386H39.1196C38.5328 4.4126 37.4221 3.88867 36.2485 3.88867C34.6557 3.88867 33.8384 4.87366 33.5031 5.62812H33.3773C33.3773 4.75556 32.67 4.04822 31.7974 4.04822H31.4088H31.1907H29.5892C29.5399 4.04822 29.5 4.08815 29.5 4.1374V6.04222C29.5 6.09147 29.5399 6.1314 29.5892 6.1314H30.7158C30.765 6.1314 30.8049 6.17132 30.8049 6.22058V14.7235H33.3773V8.52021C33.3773 7.26278 33.8593 6.08917 35.2006 6.08917Z" fill="#131212"/><path d="M73.9473 14.8722C71.0552 14.8722 69.3576 13.489 69.2109 11.4352H71.8096C71.9773 12.2106 72.5222 12.9232 73.9473 12.9232C75.1209 12.9232 75.8544 12.3992 75.8544 11.6657C75.8544 11.058 75.2676 10.7855 74.4712 10.555L72.4174 10.0101C70.5732 9.50715 69.4624 8.75269 69.4624 7.05516C69.4624 4.77083 71.621 3.89062 73.8844 3.89062C76.7555 3.89062 78.1387 5.35763 78.3483 7.30665H75.7286C75.6448 6.53123 75.079 5.83964 73.8425 5.83964C72.8784 5.83964 72.0821 6.25879 72.0821 7.01324C72.0821 7.60005 72.606 7.85153 73.2557 8.04015L75.3933 8.58503C77.5519 9.15088 78.495 10.1359 78.495 11.5819C78.495 13.4052 76.8394 14.8722 73.9473 14.8722Z" fill="#131212"/><path d="M63.7363 14.8722C60.8442 14.8722 59.1467 13.489 59 11.4352H61.5987C61.7663 12.2106 62.3112 12.9232 63.7363 12.9232C64.9099 12.9232 65.6434 12.3992 65.6434 11.6657C65.6434 11.058 65.0566 10.7855 64.2603 10.555L62.2065 10.0101C60.3622 9.50715 59.2515 8.75269 59.2515 7.05516C59.2515 4.77083 61.4101 3.89062 63.6735 3.89062C66.5446 3.89062 67.9278 5.35763 68.1373 7.30665H65.5177C65.4339 6.53123 64.868 5.83964 63.6315 5.83964C62.6675 5.83964 61.8711 6.25879 61.8711 7.01324C61.8711 7.60005 62.3951 7.85153 63.0447 8.04015L65.1824 8.58503C67.341 9.15088 68.284 10.1359 68.284 11.5819C68.284 13.4052 66.6284 14.8722 63.7363 14.8722Z" fill="#131212"/><path d="M52.5997 14.8931C49.5076 14.8931 47.1719 12.8603 47.1719 9.36045C47.1719 5.88156 49.5076 3.89062 52.5997 3.89062C55.6473 3.89062 57.9608 5.79773 57.9608 9.36045C57.9608 12.8603 55.7363 14.8931 52.5997 14.8931ZM50.0415 9.36045C50.0415 11.3304 50.9758 12.7974 52.5997 12.7974C54.2236 12.7974 55.0912 11.3304 55.0912 9.36045C55.0912 7.36952 54.2236 6.0073 52.5997 6.0073C50.9758 6.0073 50.0415 7.36952 50.0415 9.36045Z" fill="#131212"/><path fill-rule="evenodd" clip-rule="evenodd" d="M15.0631 2.07716C15.9596 1.00349 17.1784 0.332031 18.7599 0.332031H21.4022C21.5109 0.332031 21.599 0.420242 21.599 0.529056V12.6391C21.599 15.6652 19.1483 18.1184 16.1252 18.1184H13.483C13.3743 18.1184 13.2862 18.0301 13.2862 17.9213V16.5028C13.2862 16.4576 13.2301 16.436 13.1991 16.4689C12.2432 17.4863 10.9172 18.1185 9.35767 18.1185H7.19075C7.08205 18.1185 6.99393 18.0303 6.99393 17.9215V16.5456C6.99393 16.5007 6.9384 16.4789 6.90729 16.5113C5.95458 17.5037 4.64391 18.1182 3.10634 18.1182H0.696276C0.587573 18.1182 0.499451 18.03 0.499451 17.9212V9.74028C0.499451 6.71414 2.95013 4.26098 5.9732 4.26098H8.46934C9.2996 2.93461 10.6804 2.07716 12.4677 2.07716H15.0631ZM18.7599 2.30228H19.7291V12.6391C19.7291 14.577 18.0613 16.1481 16.1252 16.1481H15.2545V5.81133C15.2545 3.87333 16.8239 2.30228 18.7599 2.30228ZM13.3526 4.04741H12.4677C10.5316 4.04741 8.96218 5.61847 8.96218 7.55646V16.1482H9.35767C11.5096 16.1482 13.3526 14.402 13.3526 12.2478V4.04741ZM7.10123 6.23123H5.9732C4.03717 6.23123 2.46771 7.80228 2.46771 9.74028V16.148H3.10634C5.2583 16.148 7.10123 14.4017 7.10123 12.2476V6.23123Z" fill="#131212"/></svg>`

const MOSS_LOGO_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="79" height="19" viewBox="0 0 79 19" fill="none"><path d="M35.2006 6.08917C36.2904 6.08917 36.9401 6.86459 36.9401 8.26872V14.7235H39.5178V8.49925C39.5178 7.13703 40.0627 6.08917 41.3411 6.08917C42.4308 6.08917 43.0805 6.86459 43.0805 8.26872V14.7235H45.7421V7.59809C45.7421 5.22993 44.2541 3.88867 42.2841 3.88867C40.8171 3.88867 39.8741 4.64313 39.2453 5.75386H39.1196C38.5328 4.4126 37.4221 3.88867 36.2485 3.88867C34.6557 3.88867 33.8384 4.87366 33.5031 5.62812H33.3773C33.3773 4.75556 32.67 4.04822 31.7974 4.04822H31.4088H31.1907H29.5892C29.5399 4.04822 29.5 4.08815 29.5 4.1374V6.04222C29.5 6.09147 29.5399 6.1314 29.5892 6.1314H30.7158C30.765 6.1314 30.8049 6.17132 30.8049 6.22058V14.7235H33.3773V8.52021C33.3773 7.26278 33.8593 6.08917 35.2006 6.08917Z" fill="white"/><path d="M73.9473 14.8722C71.0552 14.8722 69.3576 13.489 69.2109 11.4352H71.8096C71.9773 12.2106 72.5222 12.9232 73.9473 12.9232C75.1209 12.9232 75.8544 12.3992 75.8544 11.6657C75.8544 11.058 75.2676 10.7855 74.4712 10.555L72.4174 10.0101C70.5732 9.50715 69.4624 8.75269 69.4624 7.05516C69.4624 4.77083 71.621 3.89062 73.8844 3.89062C76.7555 3.89062 78.1387 5.35763 78.3483 7.30665H75.7286C75.6448 6.53123 75.079 5.83964 73.8425 5.83964C72.8784 5.83964 72.0821 6.25879 72.0821 7.01324C72.0821 7.60005 72.606 7.85153 73.2557 8.04015L75.3933 8.58503C77.5519 9.15088 78.495 10.1359 78.495 11.5819C78.495 13.4052 76.8394 14.8722 73.9473 14.8722Z" fill="white"/><path d="M63.7363 14.8722C60.8442 14.8722 59.1467 13.489 59 11.4352H61.5987C61.7663 12.2106 62.3112 12.9232 63.7363 12.9232C64.9099 12.9232 65.6434 12.3992 65.6434 11.6657C65.6434 11.058 65.0566 10.7855 64.2603 10.555L62.2065 10.0101C60.3622 9.50715 59.2515 8.75269 59.2515 7.05516C59.2515 4.77083 61.4101 3.89062 63.6735 3.89062C66.5446 3.89062 67.9278 5.35763 68.1373 7.30665H65.5177C65.4339 6.53123 64.868 5.83964 63.6315 5.83964C62.6675 5.83964 61.8711 6.25879 61.8711 7.01324C61.8711 7.60005 62.3951 7.85153 63.0447 8.04015L65.1824 8.58503C67.341 9.15088 68.284 10.1359 68.284 11.5819C68.284 13.4052 66.6284 14.8722 63.7363 14.8722Z" fill="white"/><path d="M52.5997 14.8931C49.5076 14.8931 47.1719 12.8603 47.1719 9.36045C47.1719 5.88156 49.5076 3.89062 52.5997 3.89062C55.6473 3.89062 57.9608 5.79773 57.9608 9.36045C57.9608 12.8603 55.7363 14.8931 52.5997 14.8931ZM50.0415 9.36045C50.0415 11.3304 50.9758 12.7974 52.5997 12.7974C54.2236 12.7974 55.0912 11.3304 55.0912 9.36045C55.0912 7.36952 54.2236 6.0073 52.5997 6.0073C50.9758 6.0073 50.0415 7.36952 50.0415 9.36045Z" fill="white"/><path fill-rule="evenodd" clip-rule="evenodd" d="M15.0631 2.07716C15.9596 1.00349 17.1784 0.332031 18.7599 0.332031H21.4022C21.5109 0.332031 21.599 0.420242 21.599 0.529056V12.6391C21.599 15.6652 19.1483 18.1184 16.1252 18.1184H13.483C13.3743 18.1184 13.2862 18.0301 13.2862 17.9213V16.5028C13.2862 16.4576 13.2301 16.436 13.1991 16.4689C12.2432 17.4863 10.9172 18.1185 9.35767 18.1185H7.19075C7.08205 18.1185 6.99393 18.0303 6.99393 17.9215V16.5456C6.99393 16.5007 6.9384 16.4789 6.90729 16.5113C5.95458 17.5037 4.64391 18.1182 3.10634 18.1182H0.696276C0.587573 18.1182 0.499451 18.03 0.499451 17.9212V9.74028C0.499451 6.71414 2.95013 4.26098 5.9732 4.26098H8.46934C9.2996 2.93461 10.6804 2.07716 12.4677 2.07716H15.0631ZM18.7599 2.30228H19.7291V12.6391C19.7291 14.577 18.0613 16.1481 16.1252 16.1481H15.2545V5.81133C15.2545 3.87333 16.8239 2.30228 18.7599 2.30228ZM13.3526 4.04741H12.4677C10.5316 4.04741 8.96218 5.61847 8.96218 7.55646V16.1482H9.35767C11.5096 16.1482 13.3526 14.402 13.3526 12.2478V4.04741ZM7.10123 6.23123H5.9732C4.03717 6.23123 2.46771 7.80228 2.46771 9.74028V16.148H3.10634C5.2583 16.148 7.10123 14.4017 7.10123 12.2476V6.23123Z" fill="white"/></svg>`

function svgToPng(svgString: string, width: number, height: number): Promise<string> {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas')
    const scale = 3
    canvas.width = width * scale
    canvas.height = height * scale
    const ctx = canvas.getContext('2d')!
    const img = new Image()

    img.onload = () => {
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      resolve(canvas.toDataURL('image/png'))
    }

    img.src = `data:image/svg+xml;base64,${btoa(svgString)}`
  })
}

async function addLogo(doc: jsPDF, x: number, y: number, width: number, variant: 'light' | 'dark' = 'light') {
  const height = width * (19 / 79)
  const svg = variant === 'dark' ? MOSS_LOGO_DARK_SVG : MOSS_LOGO_SVG
  const pngData = await svgToPng(svg, width * 3, height * 3)
  doc.addImage(pngData, 'PNG', x, y, width, height)
}

function drawPill(doc: jsPDF, text: string, x: number, y: number) {
  doc.setFontSize(8)
  const w = doc.getTextWidth(text) + 10
  const h = 9
  doc.setFillColor(...PILL_BG)
  doc.roundedRect(x, y, w, h, 1.4, 1.4, 'F')
  doc.setDrawColor(255, 255, 255)
  doc.setLineWidth(0.2)
  const gState = doc.GState({'stroke-opacity': 0.1})
  doc.saveGraphicsState()
  doc.setGState(gState)
  doc.roundedRect(x, y, w, h, 1.4, 1.4, 'S')
  doc.restoreGraphicsState()
  doc.setTextColor(...WHITE)
  doc.text(text, x + 5, y + h / 2 + 1)
  return w
}

function drawSectionLabel(doc: jsPDF, label: string, x: number, y: number) {
  doc.setFontSize(7)
  doc.setTextColor(...GREY_LABEL)
  doc.text(label.toUpperCase(), x, y)
}

function pdfSafe(text: string): string {
  return text
    .replace(/≥/g, '>=')
    .replace(/≤/g, '<=')
    .replace(/≈/g, '~')
    .replace(/→/g, '->')
    .replace(/−/g, '-')
    .replace(/—/g, '-')
}

function drawDivider(doc: jsPDF, margin: number, y: number, width: number) {
  doc.setDrawColor(...LIGHT_BORDER)
  doc.setLineWidth(0.3)
  doc.line(margin, y, margin + width, y)
}

function addFooter(doc: jsPDF) {
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  doc.setFillColor(...MOSS_GREEN)
  doc.rect(0, pageHeight - 14, pageWidth, 14, 'F')
  doc.setFontSize(7)
  doc.setFont(FONT_NAME, 'normal')
  doc.setTextColor(...WHITE)
  const creationDate = new Date().toLocaleDateString('en-GB', {day: 'numeric', month: 'long', year: 'numeric'})
  doc.text(`Generated with Moss Per Diem Calculator  |  ${creationDate}`, 20, pageHeight - 5)
  doc.text('getmoss.com', pageWidth - 20, pageHeight - 5, {align: 'right'})
}

function checkPageBreak(doc: jsPDF, y: number, needed: number, margin: number): number {
  const pageHeight = doc.internal.pageSize.getHeight()

  if (y + needed > pageHeight - 20) {
    addFooter(doc)
    doc.addPage()
    return margin
  }

  return y
}

export async function buildPdfDoc(result: CalcResult, country: Country, destMode: Destination, intlDest: string) {
  const doc = new jsPDF()
  await loadFonts(doc)
  const pageWidth = doc.internal.pageSize.getWidth()
  const margin = 20
  const contentWidth = pageWidth - margin * 2

  // ── Header ──
  await addLogo(doc, pageWidth - margin - 30, 16, 30, 'dark')

  // ── Headline ──
  doc.setFontSize(22)
  doc.setTextColor(19, 18, 18)
  doc.text('Per Diem Allowance', margin, 38)
  doc.setFontSize(10)
  doc.setTextColor(...GREY_LABEL)
  doc.text('Tax-free travel allowance summary for payroll processing', margin, 44)

  const pageH = doc.internal.pageSize.getHeight()
  const footerZone = 20
  let y = 56

  // ── Total banner (inside card) ──
  const destination =
    destMode === 'international' && intlDest
      ? `${COUNTRY_NAMES[country]} to ${intlDest.includes(' - ') ? intlDest.replace(' - ', ' (') + ')' : intlDest}`
      : COUNTRY_NAMES[country]

  const metaParts = result.meta ? result.meta.split('·').map((s) => s.trim()) : []
  const pills = [destination, ...metaParts.filter((p) => p !== intlDest && p !== 'Domestic')]
  const pad = 8
  const pillBottom = pad + 5 + 17 + 7 + 9
  const bannerHeight = pillBottom + pad

  doc.setFillColor(...BANNER_BG)
  doc.roundedRect(margin, y, contentWidth, bannerHeight, 2.8, 2.8, 'F')

  // Label — white 50% opacity
  doc.setFontSize(7)
  const labelGState = doc.GState({opacity: 0.5})
  doc.saveGraphicsState()
  doc.setGState(labelGState)
  doc.setTextColor(...WHITE)
  doc.text('TOTAL TAX-FREE ALLOWANCE', margin + 12, y + pad + 5)
  doc.restoreGraphicsState()

  // Amount — white, large, bold
  doc.setFontSize(36)
  doc.setFont(FONT_NAME, 'bold')
  doc.setTextColor(...WHITE)
  doc.text(`${result.currency} ${result.total.toFixed(2)}`, margin + 12, y + pad + 5 + 17)
  doc.setFont(FONT_NAME, 'normal')

  // Pills row
  let pillX = margin + 12

  pills.forEach((pill) => {
    const w = drawPill(doc, pill, pillX, y + pad + 5 + 17 + 7)
    pillX += w + 3
  })

  y += bannerHeight + 8

  // ── Breakdown ──
  const rowH = 10
  const tableW = contentWidth

  if (result.items.length > 0) {
    const tableH = result.items.length * rowH
    const lastIdx = result.items.length - 1

    y = checkPageBreak(doc, y, tableH + 14, margin)
    drawSectionLabel(doc, 'Breakdown', margin, y)
    y += 4

    // Total row fill
    result.items.forEach((item, i) => {
      if (!item.isTotal) {
        return
      }

      const rowY = y + i * rowH
      doc.setFillColor(240, 240, 240)

      if (i === lastIdx) {
        doc.roundedRect(margin + 0.15, rowY - rowH, tableW - 0.3, rowH * 2, 2.8, 2.8, 'F')
        doc.setFillColor(255, 255, 255)
        doc.rect(margin + 0.15, rowY - rowH, tableW - 0.3, rowH, 'F')
        doc.setFillColor(240, 240, 240)
        doc.rect(margin + 0.15, rowY, tableW - 0.3, rowH - 2.8, 'F')
      } else if (i === 0) {
        doc.roundedRect(margin + 0.15, rowY, tableW - 0.3, rowH * 2, 2.8, 2.8, 'F')
        doc.setFillColor(255, 255, 255)
        doc.rect(margin + 0.15, rowY + rowH, tableW - 0.3, rowH, 'F')
        doc.setFillColor(240, 240, 240)
        doc.rect(margin + 0.15, rowY + 2.8, tableW - 0.3, rowH - 2.8, 'F')
      } else {
        doc.rect(margin + 0.15, rowY, tableW - 0.3, rowH, 'F')
      }
    })

    // Outer border with radius
    doc.setDrawColor(...LIGHT_BORDER)
    doc.setLineWidth(0.3)
    doc.roundedRect(margin, y, tableW, tableH, 2.8, 2.8, 'S')

    // Rows
    result.items.forEach((item, i) => {
      const rowY = y + i * rowH

      if (i > 0) {
        doc.setDrawColor(...LIGHT_BORDER)
        doc.setLineWidth(0.2)
        doc.line(margin, rowY, margin + tableW, rowY)
      }

      doc.setFontSize(9)

      if (item.isTotal) {
        doc.setFont(FONT_NAME, 'bold')
        doc.setTextColor(0, 0, 0)
      } else {
        doc.setFont(FONT_NAME, 'normal')
        doc.setTextColor(...BODY_TEXT)
      }

      doc.text(item.label, margin + 8, rowY + 6.5)

      if (item.description && !item.isTotal) {
        const labelW = doc.getTextWidth(item.label)
        doc.setFontSize(7)
        doc.setTextColor(...GREY_LABEL)

        const desc =
          intlDest && item.description.includes(intlDest)
            ? item.description.replace(
                intlDest,
                intlDest.includes(' - ') ? intlDest.replace(' - ', ' (') + ')' : intlDest,
              )
            : item.description

        doc.text(desc, margin + 8 + labelW + 4, rowY + 6.5)
      }

      doc.setFontSize(9)
      doc.setFont(FONT_NAME, 'bold')

      if (item.type === 'neg' && !item.isTotal) {
        doc.setTextColor(...NEG_TEXT)
      } else if (item.type === 'zero') {
        doc.setTextColor(...GREY_LABEL)
      } else {
        doc.setTextColor(
          item.isTotal ? 0 : BODY_TEXT[0],
          item.isTotal ? 0 : BODY_TEXT[1],
          item.isTotal ? 0 : BODY_TEXT[2],
        )
      }

      const amountText =
        item.value === 0 ? '–' : `${item.type === 'neg' ? '– ' : ''}${fmt(item.value, result.currency)}`

      doc.text(amountText, margin + tableW - 8, rowY + 6.5, {align: 'right'})
      doc.setFont(FONT_NAME, 'normal')
    })

    y += tableH
  }

  // ── Rules applied ──
  if (result.ruleInfo) {
    y += 8
    y = checkPageBreak(doc, y, 40, 20)
    drawDivider(doc, margin, y, contentWidth)
    y += 8
    drawSectionLabel(doc, 'Rules applied', margin, y)
    y += 7

    const plainRules = pdfSafe(
      result.ruleInfo
        .replace(/<[^>]*>/g, '')
        .replace(/&amp;/g, '&')
        .replace(/&nbsp;/g, ' '),
    )

    doc.setFontSize(10)
    doc.setTextColor(...BODY_TEXT)
    const rulesLines = doc.splitTextToSize(plainRules, contentWidth)
    const rulesLineH = 5.6

    rulesLines.forEach((line: string) => {
      if (y + rulesLineH > pageH - footerZone) {
        addFooter(doc)
        doc.addPage()
        y = margin
      }

      doc.text(line, margin, y)
      y += rulesLineH
    })

    y -= rulesLines.length * 1.4
  }

  // ── Daily breakdown ──
  if (result.log && result.log.length > 0 && result.log.some((d) => d.amount !== 0)) {
    y += 8
    y = checkPageBreak(doc, y, 30, 20)
    drawDivider(doc, margin, y, contentWidth)
    y += 8
    drawSectionLabel(doc, 'Daily breakdown', margin, y)
    y += 4

    let tableStartY = y

    function drawDailyHeader() {
      // Header background with rounded top corners
      doc.setFillColor(240, 240, 240)
      doc.roundedRect(margin + 0.15, y, tableW - 0.3, rowH * 2, 2.8, 2.8, 'F')
      doc.setFillColor(255, 255, 255)
      doc.rect(margin + 0.15, y + rowH, tableW - 0.3, rowH, 'F')
      doc.setFillColor(240, 240, 240)
      doc.rect(margin + 0.15, y + 2.8, tableW - 0.3, rowH - 2.8, 'F')

      doc.setFontSize(7)
      doc.setFont(FONT_NAME, 'bold')
      doc.setTextColor(...GREY_LABEL)
      doc.text('DATE', margin + 8, y + 6.5)
      doc.text('TYPE', margin + 32, y + 6.5)
      doc.text('HOURS', margin + tableW - 38, y + 6.5, {align: 'right'})
      doc.text('AMOUNT', margin + tableW - 8, y + 6.5, {align: 'right'})

      y += rowH
    }

    function closeDailyTable() {
      doc.setDrawColor(...LIGHT_BORDER)
      doc.setLineWidth(0.3)
      doc.roundedRect(margin, tableStartY, tableW, y - tableStartY, 2.8, 2.8, 'S')
    }

    drawDailyHeader()

    result.log.forEach((day) => {
      if (y + rowH > pageH - footerZone) {
        closeDailyTable()
        addFooter(doc)
        doc.addPage()
        y = margin
        tableStartY = y
        drawDailyHeader()
      }

      doc.setDrawColor(...LIGHT_BORDER)
      doc.setLineWidth(0.2)
      doc.line(margin, y, margin + tableW, y)

      doc.setFontSize(9)
      doc.setFont(FONT_NAME, 'bold')
      doc.setTextColor(...GREY_LABEL)
      doc.text(day.date, margin + 8, y + 6.5)

      doc.setFont(FONT_NAME, 'normal')
      doc.setTextColor(...BODY_TEXT)
      doc.text(pdfSafe(day.type), margin + 32, y + 6.5)

      doc.setFont(FONT_NAME, 'normal')
      doc.setTextColor(...GREY_LABEL)
      doc.text(`${day.hours}h`, margin + tableW - 38, y + 6.5, {align: 'right'})

      doc.setFont(FONT_NAME, 'bold')
      doc.setTextColor(0, 0, 0)
      doc.text(`${result.currency} ${day.amount.toFixed(2)}`, margin + tableW - 8, y + 6.5, {align: 'right'})

      y += rowH
    })

    closeDailyTable()
    y += 6
  }

  // ── Footer on last page ──
  addFooter(doc)

  // ── Pagination ──
  const totalPages = doc.getNumberOfPages()

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i)
    doc.setFontSize(7)
    doc.setFont(FONT_NAME, 'normal')
    doc.setTextColor(...WHITE)
    const pageHeight = doc.internal.pageSize.getHeight()
    doc.text(`${i} / ${totalPages}`, pageWidth / 2, pageHeight - 5, {align: 'center'})
  }

  return doc
}

export async function exportResultsPdf(result: CalcResult, country: Country, destMode: Destination, intlDest: string) {
  const doc = await buildPdfDoc(result, country, destMode, intlDest)
  doc.save('per-diem-allowance.pdf')
}
